// src/utils/ipix.js

/**
 * Utility untuk membangun URL IPX dengan modifiers
 */

// Base URL IPX service yang digunakan
export const DEFAULT_BASE = 'https://ipx-direct.sarbeh.com/_ipx';

const FALLBACK_ENABLED = true;
const MAX_RETRY_ATTEMPTS = 10;

// Map dari nama opsi friendly ke kode short IPX
export const KEY_MAP = {
  width: 'w',
  height: 'h',
  quality: 'q',
  format: 'f',
}

/**
 * Bangun URL IPX dengan fallback mechanism:
 *   /_ipx/{modifiers}/{encodeURIComponent(src)}
 *
 * @param {string} src  URL asli gambar (raw)
 * @param {Object} opts Opsi IPX { width, height, quality, format }
 * @param {string} base Base URL IPX (DEFAULT_BASE)
 * @returns {string} URL lengkap dengan modifiers
 */
export function ipxUrl(src, opts = {}, base = DEFAULT_BASE) {
  // Validasi input
  if (!src || typeof src !== 'string') {
    console.warn('ipxUrl: Invalid src provided', src);
    return src || '';
  }

  try {
    const parts = Object.entries(opts)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${KEY_MAP[k]}_${v}`);

    const modifier = parts.join(',');
    const encoded = encodeURIComponent(src);

    // Jika tidak ada modifier, kembalikan src asli
    if (!modifier) {
      return FALLBACK_ENABLED ? src : `${base}//${encoded}`;
    }

    return `${base}/${modifier}/${encoded}`;
  } catch (error) {
    console.warn('ipxUrl: Error building URL', error);
    return FALLBACK_ENABLED ? src : '';
  }
}

/**
 * Generate atribut srcSet untuk responsive images via IPX dengan error handling
 *
 * @param {string} src          URL asli gambar
 * @param {number[]} widths     Array daftar width (px), misal [240,320,480]
 * @param {Object} [options]    Opsi tambahan: { quality, format, height }
 * @param {number} [options.quality]
 * @param {string} [options.format]
 * @param {number} [options.height]
 * @param {string} [base]
 * @returns {string} srcSet string, contoh: "... 240w, ... 320w, ... 480w"
 */
export function generateSrcSet(src, widths, { quality, format, height } = {}, base = DEFAULT_BASE) {
  if (!src || !Array.isArray(widths) || widths.length === 0) {
    console.warn('generateSrcSet: Invalid parameters', { src, widths });
    return '';
  }

  try {
    return widths
      .map((w) => {
        const opts = { width: w };
        if (height != null) opts.height = height;
        else if (w) opts.height = Math.round(w * 0.75); // Default aspect ratio 4:3

        if (quality != null) opts.quality = quality;
        if (format != null) opts.format = format;

        const url = ipxUrl(src, opts, base);
        return `${url} ${w}w`;
      })
      .join(', ');
  } catch (error) {
    console.warn('generateSrcSet: Error generating srcSet', error);
    return FALLBACK_ENABLED ? `${src} ${widths[0]}w` : '';
  }
}

/**
 * Default sizes attribute sesuai breakpoints Tailwind:
 *   - md (>=768px): 480px
 *   - sm (>=640px): 320px
 *   - base (<640px): 240px
 */
export const defaultSizes = '(min-width: 768px) 480px, (min-width: 640px) 320px, 240px';
